import { useCallback, useEffect, useState } from 'react';
import API_ENDPOINTS from '../config/api';

// One copy of each instance row per page, shared by every component that
// reads or switches its settings (the run strip's lock, Työpaja's modules,
// module controls), so a change made in one place shows in the others.
const records = new Map(); // instanceId → { instance, error, promise, listeners:Set }

function entryFor(instanceId) {
  if (!records.has(instanceId)) {
    records.set(instanceId, { instance: null, error: null, promise: null, listeners: new Set() });
  }
  return records.get(instanceId);
}

function notify(entry) {
  entry.listeners.forEach((listener) => listener());
}

function load(instanceId, { force = false } = {}) {
  const entry = entryFor(instanceId);
  if (entry.promise && !force) return entry.promise;
  // credentials: an admin session gets settings.pin / reportPin (#836)
  entry.promise = fetch(API_ENDPOINTS.INSTANCE(instanceId), { credentials: 'include' })
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    })
    .then((instance) => { entry.instance = instance; entry.error = null; })
    .catch((err) => { entry.error = err; })
    .finally(() => notify(entry));
  return entry.promise;
}

/** Read a dotted key ("aiFeatures.webSearchSolutionAnalysis") from an object. */
export function readPath(obj, dottedKey) {
  return dottedKey.split('.').reduce((acc, part) => (acc == null ? undefined : acc[part]), obj);
}

/**
 * The top-level patch that sets a dotted key. The backend merges settings and
 * metadata one level deep, so a nested key sends its whole parent object.
 */
export function buildPatch(current, dottedKey, value) {
  const [head, ...rest] = dottedKey.split('.');
  if (rest.length === 0) return { [head]: value };
  const parent = { ...(current?.[head] || {}) };
  let cursor = parent;
  rest.slice(0, -1).forEach((part) => {
    cursor[part] = { ...(cursor[part] || {}) };
    cursor = cursor[part];
  });
  cursor[rest[rest.length - 1]] = value;
  return { [head]: parent };
}

/**
 * useInstanceRecord(instanceId) → { instance, error, loading, patch, setValue, setStatus, reload }
 *
 * - patch(body): PATCH API_ENDPOINTS.INSTANCE; the response row replaces the copy.
 * - setValue('settings' | 'metadata', dottedKey, value): one switch, built
 *   with buildPatch so sibling keys survive.
 * - setStatus(status): the lifecycle status (its own endpoint), then reload.
 */
export default function useInstanceRecord(instanceId) {
  const [, force] = useState(0);

  useEffect(() => {
    if (!instanceId) return undefined;
    const entry = entryFor(instanceId);
    const listener = () => force((n) => n + 1);
    entry.listeners.add(listener);
    load(instanceId);
    return () => entry.listeners.delete(listener);
  }, [instanceId]);

  const entry = instanceId ? entryFor(instanceId) : null;

  const patch = useCallback(async (body) => {
    const res = await fetch(API_ENDPOINTS.INSTANCE(instanceId), {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const target = entryFor(instanceId);
    target.instance = await res.json();
    notify(target);
    return target.instance;
  }, [instanceId]);

  const setValue = useCallback((store, dottedKey, value) => {
    const current = entryFor(instanceId).instance?.[store];
    return patch({ [store]: buildPatch(current, dottedKey, value) });
  }, [instanceId, patch]);

  const reload = useCallback(() => load(instanceId, { force: true }), [instanceId]);

  const setStatus = useCallback(async (status) => {
    const res = await fetch(API_ENDPOINTS.INSTANCE_STATUS(instanceId), {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    await load(instanceId, { force: true });
  }, [instanceId]);

  return {
    instance: entry?.instance || null,
    error: entry?.error || null,
    loading: Boolean(instanceId) && !entry?.instance && !entry?.error,
    patch,
    setValue,
    setStatus,
    reload,
  };
}
